import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Coins, Send, Download, Wallet, FileCheck2, Check } from 'lucide-react';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { StatCard, PageHeader } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { getAging, listReceivables, type Receivable } from './receivables.api';
import { addPayment, type PaymentMethod } from '../sales/sales.api';
import { useCurrentSession } from '../cash-sessions/use-current-session';

const fmt = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AVC = ['#84CC16', '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4'];
const initials = (n: string): string =>
  (n || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const BUCKETS = [
  { key: 'current', label: 'Corriente', color: '#74D7A8' },
  { key: 'd1_30', label: '1–30 días', color: '#B9DE83' },
  { key: 'd31_60', label: '31–60 días', color: '#F4C56E' },
  { key: 'd61_90', label: '61–90 días', color: '#F6AE79' },
  { key: 'd90_plus', label: '90+ días', color: '#F1979A' },
] as const;

export function ReceivablesPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [payTarget, setPayTarget] = useState<Receivable | null>(null);

  const aging = useQuery({ queryKey: ['receivables', 'aging'], queryFn: getAging });
  const list = useQuery({
    queryKey: ['receivables', { page, overdueOnly }],
    queryFn: () => listReceivables({ page, status: 'OPEN', overdueOnly: overdueOnly || undefined }),
    placeholderData: (p) => p,
  });

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const b = aging.data?.buckets;
  const totalPorCobrar = b ? BUCKETS.reduce((s, x) => s + Number(b[x.key]), 0) : 0;
  const vencido = b ? Number(b.d1_30) + Number(b.d31_60) + Number(b.d61_90) + Number(b.d90_plus) : 0;

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Cuentas por cobrar"
          sub="Gestión de cobranza a clientes"
          actions={
            <>
              <button className="btn btn-secondary">
                <Download size={17} />
                Exportar
              </button>
              <button className="btn btn-primary">
                <Send size={17} />
                Enviar recordatorios
              </button>
            </>
          }
        />

        <div className="grid grid-collapse" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <StatCard label="Total por cobrar" value={fmt(totalPorCobrar)} icon={<Coins size={19} />} accent="#84CC16" sub={`${total} documentos`} />
          <StatCard label="Vencido" value={fmt(vencido)} icon={<AlertCircle size={19} />} accent="#EF4444" />
          <StatCard label="Documentos abiertos" value={String(total)} icon={<FileCheck2 size={19} />} accent="#22C55E" />
        </div>

        {b && (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 20 }}>
            {BUCKETS.map((x) => {
              const val = Number(b[x.key]);
              return (
                <div key={x.key} className="card card-pad fade-in" style={{ borderTop: `3px solid ${x.color}` }}>
                  <div className="t-sm muted fw6">{x.label}</div>
                  <div className="t-h1 tnum fw8" style={{ marginTop: 8, color: val > 0 ? 'var(--ink)' : 'var(--muted-2)' }}>
                    {fmt(val)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="card">
          <div className="card-head">
            <div className="card-title">Detalle de documentos</div>
            <div className="row gap6">
              <button className={`chip ${!overdueOnly ? 'on' : ''}`} onClick={() => setOverdueOnly(false)}>
                Abiertos
              </button>
              <button className={`chip ${overdueOnly ? 'on' : ''}`} onClick={() => setOverdueOnly(true)}>
                Vencidos
              </button>
            </div>
          </div>

          {list.isLoading ? (
            <div style={{ padding: '8px 0' }}>
              {Array.from({ length: 6 }).map((_, r) => (
                <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
                  {Array.from({ length: 6 }).map((_, c) => (
                    <div key={c} className="sk" style={{ height: 12, flex: c === 0 ? 2 : 1, opacity: 1 - r * 0.1 }} />
                  ))}
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <Coins size={28} />
              </div>
              <div className="muted">Sin cuentas por cobrar abiertas.</div>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Vence</th>
                    <th className="right">Total</th>
                    <th className="right">Pagado</th>
                    <th className="right">Saldo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const c = AVC[(r.customerName?.length ?? 0) % AVC.length]!;
                    const overdue = r.dueDate ? new Date(r.dueDate).getTime() < Date.now() : false;
                    return (
                      <tr key={r.id}>
                        <td>
                          <div className="row aic gap10">
                            <span className="av" style={{ width: 30, height: 30, fontSize: 11, background: `${c}22`, color: c, borderColor: `${c}33` }}>
                              {initials(r.customerName)}
                            </span>
                            <div>
                              <div className="strong">{r.customerName}</div>
                              <div className="t-xs muted tnum">
                                {r.customerDocType} {r.customerDocNumber}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {r.dueDate ? (
                            <>
                              <div className="tnum">
                                <DateDisplay value={r.dueDate} />
                              </div>
                              {overdue && <div className="t-xs neg fw6">vencido</div>}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="right tnum">
                          <Money value={r.totalAmount} />
                        </td>
                        <td className="right tnum muted">
                          <Money value={r.paidAmount} />
                        </td>
                        <td className="right tnum strong">
                          <Money value={r.balance} />
                        </td>
                        <td className="right">
                          <button className="btn btn-secondary btn-sm" onClick={() => setPayTarget(r)}>
                            <Wallet size={15} />
                            Cobrar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {total > 20 && (
            <div className="tbl-foot">
              <span>
                Mostrando {items.length} de {total}
              </span>
              <div className="row gap6">
                <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Anterior
                </button>
                <span className="t-sm muted" style={{ padding: '0 6px' }}>
                  {page} / {totalPages}
                </span>
                <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {payTarget && <PayDialog receivable={payTarget} onClose={() => setPayTarget(null)} />}
    </div>
  );
}

function PayDialog({
  receivable,
  onClose,
}: {
  receivable: Receivable;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const session = useCurrentSession();
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amount, setAmount] = useState(receivable.balance);
  const [reference, setReference] = useState('');

  const cashRequiresSession = method === 'CASH' && !session.data;

  const pay = useMutation({
    mutationFn: () =>
      addPayment(receivable.saleId, {
        method,
        amount: Number(amount) || 0,
        receivedAmount: method === 'CASH' ? Number(amount) || 0 : undefined,
        reference: reference || undefined,
      }),
    onSuccess: () => {
      toast.success('Pago registrado');
      void qc.invalidateQueries({ queryKey: ['receivables'] });
      void qc.invalidateQueries({ queryKey: ['cash-session'] });
      onClose();
    },
    onError: (err) =>
      toast.error(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          (err as Error).message,
      ),
  });

  return (
    <CxDialog
      title="Registrar cobro"
      sub={`${receivable.customerName} · ${receivable.customerDocType} ${receivable.customerDocNumber}`}
      icon={<Wallet size={20} />}
      iconTone="success"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose} disabled={pay.isPending}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" onClick={() => pay.mutate()} disabled={pay.isPending || cashRequiresSession || !amount || Number(amount) <= 0}>
            <Check size={18} />
            {pay.isPending ? 'Procesando…' : 'Confirmar'}
          </button>
        </>
      }
    >
      <div className="col gap14">
        <div className="row between aic" style={{ padding: '12px 14px', background: 'var(--canvas)', borderRadius: 11 }}>
          <span className="muted fw6 t-sm">Saldo pendiente</span>
          <span className="tnum fw8" style={{ fontSize: 20 }}>
            <Money value={receivable.balance} />
          </span>
        </div>
        <div className="field">
          <label className="label">Método de pago</label>
          <select className="select" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            <option value="CASH">Efectivo</option>
            <option value="CARD">Tarjeta</option>
            <option value="YAPE">Yape</option>
            <option value="PLIN">Plin</option>
            <option value="TRANSFER">Transferencia</option>
          </select>
          {cashRequiresSession && (
            <div className="t-xs" style={{ color: 'var(--error)' }}>
              Pago en efectivo requiere caja abierta.
            </div>
          )}
        </div>
        <div className="grid" style={{ gridTemplateColumns: method !== 'CASH' ? '1fr 1fr' : '1fr', gap: 12 }}>
          <div className="field">
            <label className="label">Monto a abonar</label>
            <input className="input tnum" type="number" inputMode="decimal" step="0.01" min="0.01" max={receivable.balance} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          {method !== 'CASH' && (
            <div className="field">
              <label className="label">Referencia</label>
              <input className="input" value={reference} autoComplete="off" onChange={(e) => setReference(e.target.value)} />
            </div>
          )}
        </div>
      </div>
    </CxDialog>
  );
}
